/**
 * Tour-target registration hook.
 *
 *   const { beaconRef, isActive } = useOnboardingStep('budget-kpi-cards');
 *   <div ref={beaconRef} data-tour="budget-kpi-cards">…</div>
 *
 * `beaconRef` is a callback ref — passing it to a JSX element registers
 * the element with the OnboardingProvider so the tour engine can
 * portal-render the beacon + tooltip on top of it. On unmount the entry
 * is cleaned up automatically.
 */
import { useCallback, useEffect, useRef } from 'react';
import { useOnboarding } from './OnboardingContext';

export function useOnboardingStep(targetAttr: string) {
  const { currentStep, registerTarget } = useOnboarding();
  const ref = useRef<HTMLElement | null>(null);

  const beaconRef = useCallback(
    (el: HTMLElement | null) => {
      ref.current = el;
      registerTarget(targetAttr, el);
    },
    [targetAttr, registerTarget],
  );

  useEffect(() => {
    return () => registerTarget(targetAttr, null);
  }, [targetAttr, registerTarget]);

  const isActive =
    currentStep?.targetAttr === targetAttr || currentStep?.id === targetAttr;

  return { beaconRef, isActive, ref };
}
